import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Form,
  FormWrapper,
  GlobalError,
} from 'components/auth-form/auth-form.styles';
import Button from 'components/button';
import { H4 } from 'components/headings';
import Layout from 'components/layout';
import SEO from 'components/seo';
import TextInput from 'components/text-input';
import ContentSection from 'components/ui/content-section';
import useAuth from 'hooks/use-auth';
import { GetStaticProps, NextPage } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { backgroundImages } from 'utils/config';
import { scrollToTop } from 'utils/helpers';
import APP_ROUTES from 'utils/routes';
import * as yup from 'yup';

const PAGE_TITLE = 'Forgot Your Login Details?';
const PAGE_DESCRIPTION = 'Retrieve your login details or reset your password.';

// Define a schema for form validation
const schema = yup
  .object()
  .shape({
    email: yup
      .string()
      .email(`Invalid email.`)
      .required('Please provide your email.'),
  })
  .defined();

type FormData = {
  email: string;
  globalError: string;
};

const PasswordReset: NextPage = () => {
  const router = useRouter();
  const { loading, resetPassword, isAuthenticated, isAnonymous } = useAuth();
  const { register, handleSubmit, setError, errors, formState, reset } =
    useForm<FormData>({
      mode: 'onBlur',
      resolver: yupResolver(schema),
    });
  const { isSubmitting, isSubmitSuccessful, touched } = formState;
  const emailRef = useRef<HTMLInputElement | null>(null);

  const onSubmit = handleSubmit(async ({ globalError, ...data }) => {
    try {
      await resetPassword(data.email);
      reset();
    } catch (error) {
      setError('globalError', {
        type: 'manual',
        message: error.message,
      });
    }
  });

  useEffect(() => {
    // Redirect authenticated users to the account page instead of showing the password reset form.
    if (isAuthenticated && !isAnonymous && !loading) {
      router.replace(APP_ROUTES.account).then(scrollToTop);
    }
  }, [loading, isAuthenticated, isAnonymous, router]);

  return (
    <Layout mainBackground={backgroundImages.hero}>
      <SEO title={PAGE_TITLE} description={PAGE_DESCRIPTION} />
      <ContentSection textAlign='center'>
        <H4 as='h1'>Forgot your login details?</H4>
        <p>
          Enter your email address below and we will send you instructions on
          how to reset your password.
        </p>
        <Form onSubmit={onSubmit} noValidate>
          <FormWrapper>
            <div role='alert'>
              <ErrorMessage
                errors={errors}
                name='globalError'
                as={GlobalError}
              />
            </div>
            {isSubmitSuccessful && (
              <GlobalError color='black' aria-live='polite'>
                If you have a registered email, you will receive a link to reset
                your password.
              </GlobalError>
            )}

            <TextInput
              label='Email address'
              id='email'
              name='email'
              type='email'
              required
              autoComplete='email'
              aria-invalid={touched.email && !!errors.email}
              disabled={isSubmitting}
              ref={(input) => {
                emailRef.current = input;
                register(input);
              }}
              errors={errors}
            />
          </FormWrapper>

          <Button type='submit' loading={isSubmitting}>
            Send me instructions
          </Button>

          <p>
            Remembered your password?{' '}
            <Link href={APP_ROUTES.users.signIn}>
              <a>Login</a>
            </Link>
          </p>

          <p>
            No account?{' '}
            <Link href={APP_ROUTES.users.signUp} passHref>
              <Button as='a' variant='secondary'>
                Create an account
              </Button>
            </Link>
          </p>
        </Form>
      </ContentSection>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {},
  };
};

export default PasswordReset;
